/**
 * VoxPont experimental model registry (Proximity Brief v6 §§17–22).
 *
 * WHAT: Declarative manifests for VP110–VP430. Model numbers are permanent IDs.
 * WHY: Compose capabilities instead of forking engines. Auth engines 110–430 map here.
 * INVARIANT: experimental:true for all entries. Unsupported capabilities stay
 *            status "not_built" or "simulated" until physical proof.
 */

/** @typedef {'built'|'partial'|'simulated'|'not_built'|'feasibility_only'} CapabilityStatus */

/**
 * @typedef {'connection_audio'|'translation_interaction'|'combination'} VpModelCategory
 *
 * @typedef {Object} VpModelManifest
 * @property {string} modelId
 * @property {string} engineId — numeric auth/route id (e.g. "130")
 * @property {number} number
 * @property {string} modelName
 * @property {VpModelCategory} category
 * @property {string} purpose
 * @property {boolean} experimental
 * @property {boolean} enabled — login-selector visibility (product-wide)
 * @property {string[]} requiredCapabilities
 * @property {string[]} optionalCapabilities
 * @property {string[]} [fallbackChain]
 * @property {string[]} [components]
 * @property {string} [audioPolicy]
 * @property {{ min: number, max: number, admit: string }} [participantPolicy]
 * @property {Record<string, boolean>} [featureFlags]
 * @property {CapabilityStatus} status
 * @property {string} [mapsToExisting]
 * @property {string} [notes]
 */

/** Canonical ordered catalog — exactly these 33 IDs must remain registered. */
export const EXPECTED_VP_MODEL_IDS = Object.freeze([
  'VP110', 'VP120', 'VP130', 'VP140', 'VP150', 'VP160', 'VP170', 'VP180',
  'VP190', 'VP200', 'VP210', 'VP220', 'VP230', 'VP240', 'VP250', 'VP260',
  'VP270', 'VP280', 'VP290', 'VP300', 'VP310', 'VP320', 'VP330',
  'VP340', 'VP350', 'VP360', 'VP370', 'VP380', 'VP390', 'VP400', 'VP410',
  'VP420', 'VP430',
]);

export const VP_MODEL_CATEGORY_LABELS = Object.freeze({
  connection_audio: 'Connection & Audio',
  translation_interaction: 'Translation & Interaction Capabilities',
  combination: 'Combination Experiences',
});

/** @param {string} modelId */
export function deriveVpModelCategory(modelId) {
  const n = Number(String(modelId || '').replace(/^VP/i, ''));
  if (n >= 110 && n <= 180) return 'connection_audio';
  if (n >= 190 && n <= 330) return 'translation_interaction';
  if (n >= 340 && n <= 430) return 'combination';
  return 'translation_interaction';
}

/** Login badge from registry status (Phase II). */
export function resolveVpReadinessBadge(manifest) {
  const status = manifest?.status;
  if (status === 'built') return 'Ready';
  if (status === 'feasibility_only') return 'Platform Restricted';
  if (status === 'simulated') return 'Device Test';
  if (status === 'partial') return 'Experimental';
  return 'Limited';
}

/** Login / Lab label: `VP110 — Touch` */
export function formatVpSelectorLabel(manifest) {
  if (!manifest?.modelId) return '';
  return `${manifest.modelId} — ${manifest.modelName}`;
}

/** Shared capability catalog — status reflects the live experimental paired repo. */
export const VP_CAPABILITY_CATALOG = Object.freeze({
  connectionLink: { status: 'built', notes: 'QR / 6-char / paste via voxpontRooms' },
  connectionNearby: { status: 'simulated', notes: 'SimulatedBLEAdapter + Expo BLE; radio PASS pending' },
  connectionTouch: { status: 'simulated', notes: 'SimulatedUWBAdapter + UWB stub; radio PASS pending' },
  audioSplitStereo: { status: 'partial', notes: 'Web Audio pan in Audio Lab; not hardware-proven' },
  audioEarbudSpeaker: {
    status: 'feasibility_only',
    notes: 'Concurrent BT + speaker not available in browser',
  },
  audioDualBluetooth: {
    status: 'feasibility_only',
    notes: 'Unsupported on web; recommend VP170',
  },
  audioTwoPhone: { status: 'partial', notes: 'Room + LIVE ASR/MT behind flag; device PASS pending' },
  audioWhisper: {
    status: 'feasibility_only',
    notes: 'Asymmetric earbud/speaker; OS route feasibility pending',
  },
  languageDetector: {
    status: 'partial',
    notes: 'Shell AutoLanguage + Core2/204 detection contract',
  },
  faceToFaceUi: { status: 'partial', notes: '180° split + shared sessionExperience; live mic pending' },
  earbudUx: {
    status: 'partial',
    notes: 'Personal-route UX with speaker/text fallback; dual-sink blocked on web',
  },
  voiceCharacter: {
    status: 'partial',
    notes: 'VoicePreservationProvider + disclosure; cloning provider_blocked',
  },
  ambiguityClarify: {
    status: 'partial',
    notes: 'TrustGuard clarify / ask-the-speaker',
  },
  backTranslate: { status: 'partial', notes: 'Turn verification back-translation' },
  numbersExtract: { status: 'partial', notes: 'Medical numeric/dose extraction' },
  sessionMemory: { status: 'partial', notes: 'Ephemeral Pair/shell store; Core2/12 opt-in deferred' },
  contextPacks: {
    status: 'partial',
    notes: 'Terminology + TG policy + MT terminology injection',
  },
  groupRoom: { status: 'partial', notes: 'Fan-out + N≤8 multi-use invites; multi-device PASS pending' },
  broadcast: { status: 'partial', notes: 'Role-gated fan-out + Broadcast UX; multi-device PASS pending' },
  lensVision: {
    status: 'partial',
    notes: 'OCR→MT→vision VpUtterance; mock default; live OCR provider_blocked until seeded',
  },
  replay: { status: 'partial', notes: 'ReplayController text modes on shared VpUtterance' },
  idiomClarify: { status: 'partial', notes: 'TrustGuard clarify / idiom advisory' },
  offlineRescue: {
    status: 'partial',
    notes: 'In-shell provider registry online→offline; packs may be missing',
  },
});

function model(partial) {
  const modelId = String(partial.modelId || '').trim();
  const number = Number(modelId.replace(/^VP/i, ''));
  return Object.freeze({
    experimental: true,
    enabled: true,
    requiredCapabilities: [],
    optionalCapabilities: [],
    featureFlags: {},
    status: 'not_built',
    ...partial,
    modelId,
    number,
    engineId: String(number),
    category: partial.category || deriveVpModelCategory(modelId),
  });
}

/** @type {ReadonlyArray<VpModelManifest>} */
export const VP_MODEL_MANIFESTS = Object.freeze([
  model({
    modelId: 'VP110',
    modelName: 'Touch',
    purpose: 'Bring two phones together to initiate an ephemeral VoxPont conversation.',
    requiredCapabilities: ['connectionTouch'],
    optionalCapabilities: ['connectionNearby', 'connectionLink'],
    fallbackChain: ['VP110', 'VP120', 'VP130'],
    participantPolicy: { min: 2, max: 2, admit: 'confirm' },
    featureFlags: { enableTouchSim: true },
    status: 'simulated',
  }),
  model({
    modelId: 'VP120',
    modelName: 'Nearby',
    purpose: 'Discover and connect nearby VoxPont users without requiring physical contact.',
    requiredCapabilities: ['connectionNearby'],
    optionalCapabilities: ['connectionLink'],
    fallbackChain: ['VP120', 'VP130'],
    participantPolicy: { min: 2, max: 2, admit: 'confirm' },
    featureFlags: { enableNearbySim: true },
    status: 'simulated',
  }),
  model({
    modelId: 'VP130',
    modelName: 'Link',
    purpose: 'Universal QR code / deep-link pairing across iOS, Android, PWA, tablet, or desktop.',
    requiredCapabilities: ['connectionLink'],
    fallbackChain: ['VP130'],
    participantPolicy: { min: 2, max: 2, admit: 'autoAdmit' },
    featureFlags: { enableLink: true },
    status: 'built',
  }),
  model({
    modelId: 'VP140',
    modelName: 'Split',
    purpose: 'One stereo earbud pair: language A left channel, B right channel.',
    requiredCapabilities: ['audioSplitStereo'],
    audioPolicy: 'stereo_left_a_right_b',
    featureFlags: { enableAudioLab: true },
    status: 'partial',
  }),
  model({
    modelId: 'VP150',
    modelName: 'Interpreter',
    purpose: 'Private earbud translation vs phone speaker for the other participant.',
    requiredCapabilities: ['audioEarbudSpeaker'],
    audioPolicy: 'earbud_private_speaker_public',
    status: 'feasibility_only',
  }),
  model({
    modelId: 'VP160',
    modelName: 'Dual',
    purpose: 'One phone routing to two independently addressable Bluetooth endpoints.',
    requiredCapabilities: ['audioDualBluetooth'],
    audioPolicy: 'two_bt_endpoints',
    status: 'feasibility_only',
  }),
  model({
    modelId: 'VP170',
    modelName: 'Pair',
    purpose: 'Two connected phones; each participant uses own phone/earbuds.',
    requiredCapabilities: ['audioTwoPhone', 'connectionLink'],
    optionalCapabilities: ['connectionNearby', 'connectionTouch'],
    audioPolicy: 'local_device_route',
    participantPolicy: { min: 2, max: 2, admit: 'autoAdmit' },
    status: 'partial',
  }),
  model({
    modelId: 'VP180',
    modelName: 'Whisper',
    purpose: 'Other person private in-ear; user reply aloud from phone.',
    requiredCapabilities: ['audioWhisper'],
    audioPolicy: 'asymmetric_earbud_then_speaker',
    status: 'feasibility_only',
  }),
  model({
    modelId: 'VP190',
    modelName: 'AutoLanguage',
    purpose: 'Identify each speaker language and adapt when it changes.',
    requiredCapabilities: ['languageDetector'],
    mapsToExisting: 'features/voxpontCore2/autoSpeakerDetection',
    status: 'partial',
  }),
  model({
    modelId: 'VP200',
    modelName: 'Face-to-Face',
    purpose: '180-degree split UI between two people on one phone.',
    requiredCapabilities: ['faceToFaceUi'],
    status: 'partial',
    notes: 'Skeleton split layout shipped; full live translation pending',
  }),
  model({
    modelId: 'VP210',
    modelName: 'Earbud',
    purpose: 'Continuous conversational translation through personal earbuds.',
    requiredCapabilities: ['earbudUx'],
    status: 'partial',
    notes: 'Personal-route UX + fallback; concurrent BT blocked on web',
  }),
  model({
    modelId: 'VP220',
    modelName: 'Voice',
    purpose: 'Preserve vocal characteristics in translated synthetic speech.',
    requiredCapabilities: ['voiceCharacter'],
    status: 'partial',
    notes: 'Standard TTS + disclosure; cloning provider_blocked',
  }),
  model({
    modelId: 'VP230',
    modelName: 'Did You Mean?',
    purpose: 'Ask for clarification on consequential ambiguity before delivery.',
    requiredCapabilities: ['ambiguityClarify'],
    mapsToExisting: 'features/voxpontCore2/clarification',
    status: 'partial',
  }),
  model({
    modelId: 'VP240',
    modelName: 'BackTranslate',
    purpose: 'Silent A→B→A semantic check; flag material divergence.',
    requiredCapabilities: ['backTranslate'],
    mapsToExisting: 'voxpont turn verification back-translation',
    status: 'partial',
  }),
  model({
    modelId: 'VP250',
    modelName: 'Numbers',
    purpose: 'Extract doses, dates, currencies, measurements alongside translation.',
    requiredCapabilities: ['numbersExtract'],
    mapsToExisting: 'voxPontMedicalText / pharmacy engines',
    status: 'partial',
  }),
  model({
    modelId: 'VP260',
    modelName: 'Session Memory',
    purpose: 'Temporary session context then discard unless saved.',
    requiredCapabilities: ['sessionMemory'],
    status: 'partial',
    notes: 'Ephemeral local skeleton; not persisted',
  }),
  model({
    modelId: 'VP270',
    modelName: 'Context Packs',
    purpose: 'Domain terminology packs (Medical, Travel, Legal, …).',
    requiredCapabilities: ['contextPacks'],
    mapsToExisting: 'domain engines 12/20/30/40/50/60/70',
    status: 'partial',
  }),
  model({
    modelId: 'VP280',
    modelName: 'Group',
    purpose: 'Multi-participant multilingual room with speaker attribution.',
    requiredCapabilities: ['groupRoom'],
    participantPolicy: { min: 2, max: 8, admit: 'hostApproval' },
    status: 'partial',
    notes: 'Fan-out + N≤8 multi-use invites; multi-device PASS pending',
  }),
  model({
    modelId: 'VP290',
    modelName: 'Broadcast',
    purpose: 'One speaker to many listeners in preferred languages.',
    requiredCapabilities: ['broadcast'],
    status: 'partial',
    notes: 'Broadcaster/listener UX + fan-out; multi-device PASS pending',
  }),
  model({
    modelId: 'VP300',
    modelName: 'Lens',
    purpose: 'Camera translation of signs, menus, documents in conversation context.',
    requiredCapabilities: ['lensVision'],
    status: 'partial',
    notes: 'OCR→MT→vision utterance; live OCR provider_blocked until credentials',
  }),
  model({
    modelId: 'VP310',
    modelName: 'Replay',
    purpose: 'Replay prior translation, slow down, original, or alternate.',
    requiredCapabilities: ['replay'],
    status: 'partial',
    notes: 'Replay last-utterance skeleton',
  }),
  model({
    modelId: 'VP320',
    modelName: 'Clarify',
    purpose: 'Explain idioms/slang without silently changing meaning.',
    requiredCapabilities: ['idiomClarify'],
    status: 'partial',
    notes: 'TrustGuard clarify stage demo on experimental shell',
  }),
  model({
    modelId: 'VP330',
    modelName: 'Rescue',
    purpose: 'Fall back to on-device speech/translation when connectivity degrades.',
    requiredCapabilities: ['offlineRescue'],
    mapsToExisting: 'paired provider registry + offlineLlm packs',
    status: 'partial',
    notes: 'In-shell online→offline switch; packs may be missing',
  }),
  // Combination models
  model({
    modelId: 'VP340',
    modelName: 'Seamless',
    purpose: 'Touch → Nearby → Link fallback into a two-phone conversation.',
    components: ['VP110', 'VP120', 'VP130', 'VP170', 'VP190'],
    requiredCapabilities: ['connectionLink', 'audioTwoPhone'],
    optionalCapabilities: ['connectionNearby', 'connectionTouch', 'languageDetector'],
    fallbackChain: ['VP110', 'VP120', 'VP130'],
    featureFlags: { enableSeamlessFallback: true },
    status: 'partial',
  }),
  model({
    modelId: 'VP350',
    modelName: 'Private Interpreter',
    purpose: 'Private whisper translation with AutoLanguage, earbuds, and session memory.',
    components: ['VP180', 'VP190', 'VP210', 'VP260'],
    requiredCapabilities: ['audioWhisper'],
    status: 'feasibility_only',
    notes: 'Whisper audio blocked on web; AutoLanguage + memory still compose',
  }),
  model({
    modelId: 'VP360',
    modelName: 'Clinical Bridge',
    purpose:
      'High-reliability medical conversation with Pair, TrustGuard integrity, and clinical context packs.',
    components: ['VP170', 'VP190', 'VP230', 'VP240', 'VP250', 'VP260', 'VP270', 'VP320'],
    requiredCapabilities: ['audioTwoPhone', 'ambiguityClarify', 'backTranslate', 'numbersExtract'],
    status: 'partial',
    notes: 'Compose TrustGuard stages; do not fork medical engines',
  }),
  model({
    modelId: 'VP370',
    modelName: 'TableTalk',
    purpose: 'One device as a shared bilingual Face-to-Face surface across a table.',
    components: ['VP200', 'VP190', 'VP260', 'VP310', 'VP320'],
    requiredCapabilities: ['faceToFaceUi'],
    status: 'partial',
    notes: 'Composes Face + AutoLanguage + Memory + Replay + Clarify',
  }),
  model({
    modelId: 'VP380',
    modelName: 'Travel Companion',
    purpose: 'Travel interpretation with whisper audio, lens, replay, clarify, and rescue.',
    components: ['VP180', 'VP190', 'VP300', 'VP310', 'VP320', 'VP330'],
    requiredCapabilities: ['audioWhisper', 'lensVision'],
    status: 'partial',
    notes: 'Lens+Replay+Clarify+Rescue compose; whisper blocked on web',
  }),
  model({
    modelId: 'VP390',
    modelName: 'Conversation Room',
    purpose: 'Multi-person multilingual room with context packs and replay.',
    components: ['VP280', 'VP190', 'VP260', 'VP270', 'VP310'],
    requiredCapabilities: ['groupRoom'],
    status: 'partial',
  }),
  model({
    modelId: 'VP400',
    modelName: 'LiveCast',
    purpose: 'One-to-many multilingual broadcast with AutoLanguage and rescue.',
    components: ['VP290', 'VP190', 'VP220', 'VP270', 'VP330'],
    requiredCapabilities: ['broadcast'],
    status: 'partial',
  }),
  model({
    modelId: 'VP410',
    modelName: 'Shared Interpreter',
    purpose: 'Stereo split earbud pair as a two-person shared interpreter.',
    components: ['VP140', 'VP190', 'VP210', 'VP310'],
    requiredCapabilities: ['audioSplitStereo'],
    status: 'partial',
  }),
  model({
    modelId: 'VP420',
    modelName: 'TrustGuard',
    purpose: 'Composable ambiguity, back-translate, numbers, and clarify checks.',
    components: ['VP230', 'VP240', 'VP250', 'VP320'],
    requiredCapabilities: ['ambiguityClarify', 'backTranslate', 'numbersExtract'],
    status: 'partial',
    notes: 'Middleware pipeline — not a conversation UI',
  }),
  model({
    modelId: 'VP430',
    modelName: 'Offline Bridge',
    purpose: 'Resilient two-device pairing with automatic offline Rescue handoff.',
    components: ['VP110', 'VP130', 'VP170', 'VP190', 'VP330'],
    requiredCapabilities: ['connectionLink', 'offlineRescue'],
    status: 'partial',
  }),
]);

const BY_ID = new Map(VP_MODEL_MANIFESTS.map((m) => [m.modelId, m]));

export function getVpModel(modelId) {
  if (!modelId) return null;
  return BY_ID.get(String(modelId).trim()) || null;
}

export function getVpModelByEngineId(engineId) {
  const id = String(engineId || '').trim();
  if (!id) return null;
  return getVpModel(`VP${id}`) || BY_ID.get(id) || null;
}

export function listVpModels({ status, category, enabledOnly = false } = {}) {
  let list = VP_MODEL_MANIFESTS.slice();
  if (status) list = list.filter((m) => m.status === status);
  if (category) list = list.filter((m) => m.category === category);
  if (enabledOnly) list = list.filter((m) => m.enabled !== false);
  return list;
}

/** Grouped lists for the login selector (Connection / Translation / Combination). */
export function listVpModelsGrouped({ enabledOnly = true } = {}) {
  const models = listVpModels({ enabledOnly });
  return Object.freeze({
    connection_audio: models.filter((m) => m.category === 'connection_audio'),
    translation_interaction: models.filter((m) => m.category === 'translation_interaction'),
    combination: models.filter((m) => m.category === 'combination'),
  });
}

/** Combination Includes line: `VP170 Pair · VP190 AutoLanguage · …` */
export function formatVpIncludes(manifest) {
  const comps = manifest?.components;
  if (!Array.isArray(comps) || comps.length === 0) return null;
  return comps
    .map((id) => {
      const child = getVpModel(id);
      return child ? `${id} ${child.modelName}` : id;
    })
    .join(' · ');
}

/**
 * Resolve launch config for a selected model (smoke tests + orchestration).
 * @param {string} modelId
 */
export function resolveVpLaunchConfiguration(modelId) {
  const manifest = getVpModel(modelId);
  if (!manifest) {
    throw new Error(`Unknown VP model: ${modelId}`);
  }
  return Object.freeze({
    modelId: manifest.modelId,
    engineId: manifest.engineId,
    route: `/voxpont${manifest.engineId}`,
    modelName: manifest.modelName,
    category: manifest.category,
    purpose: manifest.purpose,
    requiredCapabilities: manifest.requiredCapabilities.slice(),
    optionalCapabilities: manifest.optionalCapabilities.slice(),
    components: (manifest.components || []).slice(),
    fallbackChain: (manifest.fallbackChain || [manifest.modelId]).slice(),
    featureFlags: { ...manifest.featureFlags },
    audioPolicy: manifest.audioPolicy || null,
    participantPolicy: manifest.participantPolicy || null,
    status: manifest.status,
    selectorLabel: formatVpSelectorLabel(manifest),
    includesLabel: formatVpIncludes(manifest),
  });
}

/**
 * Resolve Touch → Nearby → Link (or any declared fallbackChain) against capability flags.
 * @param {string} preferredModelId
 * @param {{ hasLink?: boolean, hasNearby?: boolean, hasTouch?: boolean, simulatedOk?: boolean }} caps
 */
export function resolveFallbackModel(preferredModelId, caps = {}) {
  const {
    hasLink = true,
    hasNearby = false,
    hasTouch = false,
    simulatedOk = true,
  } = caps;
  const preferred = getVpModel(preferredModelId) || getVpModel('VP340') || getVpModel('VP130');
  const chain = preferred.fallbackChain || [preferred.modelId];

  for (const id of chain) {
    const m = getVpModel(id);
    if (!m) continue;
    if (id === 'VP110') {
      if (hasTouch || (simulatedOk && m.status === 'simulated')) return m;
      continue;
    }
    if (id === 'VP120') {
      if (hasNearby || (simulatedOk && m.status === 'simulated')) return m;
      continue;
    }
    if (id === 'VP130') {
      if (hasLink) return m;
    }
  }
  return getVpModel('VP130');
}

/**
 * Assert registry invariants used by tests and Lab.
 */
export function assertValidVpModelRegistry(manifests = VP_MODEL_MANIFESTS) {
  if (!Array.isArray(manifests) || manifests.length === 0) {
    throw new Error('VP model registry must be a non-empty array.');
  }
  const ids = new Set();
  const numbers = new Set();
  manifests.forEach((m) => {
    if (!m.modelId || !m.modelName) {
      throw new Error('Each VP model requires modelId and modelName.');
    }
    if (!/^VP\d{3}$/.test(m.modelId)) {
      throw new Error(`Invalid modelId: ${m.modelId}`);
    }
    if (ids.has(m.modelId)) {
      throw new Error(`Duplicate modelId: ${m.modelId}`);
    }
    if (numbers.has(m.number)) {
      throw new Error(`Duplicate model number: ${m.number}`);
    }
    ids.add(m.modelId);
    numbers.add(m.number);
    if (m.experimental !== true) {
      throw new Error(`${m.modelId} must be experimental until released.`);
    }
    if (m.enabled !== true && m.enabled !== false) {
      throw new Error(`${m.modelId} must declare enabled boolean.`);
    }
    if (!Object.keys(VP_MODEL_CATEGORY_LABELS).includes(m.category)) {
      throw new Error(`${m.modelId} has invalid category ${m.category}.`);
    }
    (m.components || []).forEach((compId) => {
      if (!manifests.some((other) => other.modelId === compId)) {
        throw new Error(`${m.modelId} references missing component ${compId}.`);
      }
    });
  });
  if (manifests.length !== EXPECTED_VP_MODEL_IDS.length) {
    throw new Error(
      `Expected ${EXPECTED_VP_MODEL_IDS.length} VP models, found ${manifests.length}.`
    );
  }
  EXPECTED_VP_MODEL_IDS.forEach((id) => {
    if (!ids.has(id)) {
      throw new Error(`Registry missing required model ${id}.`);
    }
  });
  return true;
}

assertValidVpModelRegistry();

export default {
  EXPECTED_VP_MODEL_IDS,
  VP_CAPABILITY_CATALOG,
  VP_MODEL_CATEGORY_LABELS,
  VP_MODEL_MANIFESTS,
  deriveVpModelCategory,
  formatVpSelectorLabel,
  formatVpIncludes,
  getVpModel,
  getVpModelByEngineId,
  listVpModels,
  listVpModelsGrouped,
  resolveVpLaunchConfiguration,
  resolveFallbackModel,
  resolveVpReadinessBadge,
  assertValidVpModelRegistry,
};
